import api from './api';

/**
 * Tournament Service - Handles all tournament-related API calls
 */
class TournamentService {
  /**
   * Get all tournaments with filtering and pagination
   */
  async getTournaments(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.status) params.append('status', options.status);
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.search) params.append('search', options.search);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);

      const response = await api.get(`/tournaments?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      throw error;
    }
  }

  /**
   * Get tournament by ID
   */
  async getTournamentById(id) {
    try {
      const response = await api.get(`/tournaments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament:', error);
      throw error;
    }
  }

  /**
   * Get tournament leaderboard
   */
  async getTournamentLeaderboard(id) {
    try {
      const response = await api.get(`/tournaments/${id}/leaderboard`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament leaderboard:', error);
      throw error;
    }
  }

  /**
   * Get tournament players
   */
  async getTournamentPlayers(id) {
    try {
      const response = await api.get(`/tournaments/${id}/players`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament players:', error);
      throw error;
    }
  }

  /**
   * Get tournament rounds
   */
  async getTournamentRounds(id) {
    try {
      const response = await api.get(`/tournaments/${id}/rounds`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament rounds:', error);
      throw error;
    }
  }

  /**
   * Get tournament snapshots
   */
  async getTournamentSnapshots(id) {
    try {
      const response = await api.get(`/tournaments/${id}/snapshots`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament snapshots:', error);
      throw error;
    }
  }

  /**
   * Get tournament timeline
   */
  async getTournamentTimeline(id) {
    try {
      const response = await api.get(`/tournaments/${id}/timeline`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament timeline:', error);
      throw error;
    }
  }

  /**
   * Create new tournament (admin only)
   */
  async createTournament(tournamentData) {
    try {
      const response = await api.post('/tournaments', tournamentData);
      return response.data;
    } catch (error) {
      console.error('Error creating tournament:', error);
      throw error;
    }
  }

  /**
   * Update tournament (admin only)
   */
  async updateTournament(id, updateData) {
    try {
      const response = await api.put(`/tournaments/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating tournament:', error);
      throw error;
    }
  }

  /**
   * Delete tournament (admin only)
   */
  async deleteTournament(id) {
    try {
      const response = await api.delete(`/tournaments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting tournament:', error);
      throw error;
    }
  }

  /**
   * Activate tournament (admin only)
   */
  async activateTournament(id) {
    try {
      const response = await api.post(`/tournaments/${id}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating tournament:', error);
      throw error;
    }
  }

  /**
   * Update tournament status (admin only)
   */
  async updateTournamentStatus(id, status, reason = '') {
    try {
      const response = await api.put(`/tournaments/${id}/status`, {
        status,
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error updating tournament status:', error);
      throw error;
    }
  }

  /**
   * Schedule tournament rounds (admin only)
   */
  async scheduleTournamentRounds(id, roundSchedules) {
    try {
      const response = await api.post(`/tournaments/${id}/schedule-rounds`, {
        roundSchedules
      });
      return response.data;
    } catch (error) {
      console.error('Error scheduling tournament rounds:', error);
      throw error;
    }
  }

  /**
   * Duplicate tournament (admin only)
   */
  async duplicateTournament(sourceTournamentId, newName, startDate, endDate) {
    try {
      const response = await api.post('/tournaments/duplicate', {
        sourceTournamentId,
        newName,
        startDate,
        endDate
      });
      return response.data;
    } catch (error) {
      console.error('Error duplicating tournament:', error);
      throw error;
    }
  }

  /**
   * Get tournament templates (admin only)
   */
  async getTournamentTemplates() {
    try {
      const response = await api.get('/tournaments/templates');
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament templates:', error);
      throw error;
    }
  }

  /**
   * Create tournament from template (admin only)
   */
  async createTournamentFromTemplate(templateData) {
    try {
      const response = await api.post('/tournaments/from-template', templateData);
      return response.data;
    } catch (error) {
      console.error('Error creating tournament from template:', error);
      throw error;
    }
  }

  /**
   * Get tournament status summary (admin only)
   */
  async getTournamentStatusSummary() {
    try {
      const response = await api.get('/tournaments/status-summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching tournament status summary:', error);
      throw error;
    }
  }

  /**
   * Bulk update tournament statuses (admin only)
   */
  async bulkUpdateTournamentStatuses() {
    try {
      const response = await api.post('/tournaments/bulk-update-status');
      return response.data;
    } catch (error) {
      console.error('Error performing bulk status update:', error);
      throw error;
    }
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  }

  /**
   * Format date for display
   */
  formatDate(dateString, options = {}) {
    const date = new Date(dateString);
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };
    return date.toLocaleDateString('en-US', defaultOptions);
  }

  /**
   * Format date and time for display
   */
  formatDateTime(dateString, options = {}) {
    const date = new Date(dateString);
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    return date.toLocaleDateString('en-US', defaultOptions);
  }

  /**
   * Get tournament status badge color
   */
  getStatusColor(status) {
    const colors = {
      DRAFT: '#6c757d',
      ACTIVE: '#28a745',
      LIVE: '#ff6b35',
      COMPLETED: '#007bff',
      CANCELLED: '#dc3545'
    };
    return colors[status] || '#6c757d';
  }

  /**
   * Get tournament status display text
   */
  getStatusText(status) {
    const texts = {
      DRAFT: 'Draft',
      ACTIVE: 'Active',
      LIVE: 'Live',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled'
    };
    return texts[status] || status;
  }

  /**
   * Calculate tournament progress
   */
  calculateProgress(startDate, endDate) {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (now < start) return 0;
    if (now > end) return 100;
    
    const total = end - start;
    const elapsed = now - start;
    return Math.round((elapsed / total) * 100);
  }

  /**
   * Get tournament format display text
   */
  getFormatText(format) {
    const formats = {
      STROKE_PLAY: 'Stroke Play',
      MATCH_PLAY: 'Match Play',
      SKINS: 'Skins Game',
      STABLEFORD: 'Stableford',
      CUSTOM: 'Custom'
    };
    return formats[format] || format;
  }

  /**
   * Calculate Prize Distribution V2 (per-round logic)
   */
  async calculatePrizeDistributionV2(tournamentId) {
    try {
      const response = await api.get(`/tournaments/${tournamentId}/prize-v2`);
      return response.data;
    } catch (error) {
      console.error('Error calculating prize distribution V2:', error);
      throw error;
    }
  }
}

export default new TournamentService();